import React, {useMemo} from "react";
import {Context} from "@hotwired/stimulus";
import {useForm} from "react-hook-form";
import {valibotResolver} from "@hookform/resolvers/valibot";
import {ReactWrapperController} from "@root/server/client/esweb/src/lib/stimulus/ReactWrapperController.ts";
import {Form} from "@root/server/client/esweb/components/ui/form.tsx";
import {FormInput} from "@root/server/client/esweb/components/ui/rhf/FormInput.tsx";
import {FormServerErrors} from "@root/server/client/esweb/src/pages/web/shared/form-server-errors.tsx";
import {useFormSubmission} from "@root/server/client/esweb/src/lib/hooks/useFormSubmission.ts";
import {SaveSubmitButton} from "@root/server/client/esweb/src/pages/web/shared/continue-submit-button.tsx";
import {
    rss_form_name,
    RssFeedFormProps,
    RssFeedFormSchema,
    RssFeedFormValues
} from "./rss_form.shared.ts";

export const name = rss_form_name;

export default class extends ReactWrapperController {
    constructor(context: Context) {
        super(context, (props) => <RssFeedForm {...props} />);
    }
}

function RssFeedForm({ submitUrl, initialData }: RssFeedFormProps) {
    
    const defaultValues = useMemo(() => ({
        url: initialData?.url ?? '',
        interval_m: initialData?.interval_m ?? 60
    }), [initialData]);

    const form = useForm<RssFeedFormValues>({
        resolver: valibotResolver(RssFeedFormSchema),
        defaultValues,
        mode: "onTouched",
    });

    const { control, handleSubmit } = form;
    const { execute, isSubmitting, canSubmit, serverErrors } = useFormSubmission({ 
        submitUrl
    });

    return (
        <Form {...form}>
            <form onSubmit={handleSubmit(execute)} className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                    <FormInput 
                        control={control} 
                        name="url" 
                        label="Feed URL" 
                        placeholder="https://example.com/feed.xml" 
                    />
                    <FormInput 
                        control={control} 
                        name="interval_m" 
                        type="number" 
                        label="Interval (minutes)" 
                        min={5}
                    />
                </div>

                <div className="text-sm bg-slate-50 border border-slate-200 p-3 rounded-md text-slate-600">
                    <p>
                        <strong>Note:</strong> Every run will consume a check for each item in the feed.
                        Do not forget to set <strong>Deduplication Key</strong> to <code className="bg-slate-200 px-1 rounded">id</code> in your filter settings, to avoid redundant notifications and expensive AI calls.
                    </p>
                </div>

                <FormServerErrors errors={serverErrors} />
                
                <div className="flex justify-end">
                    <SaveSubmitButton 
                        isSubmitting={isSubmitting} 
                        canSubmit={canSubmit} 
                        label={initialData ? "Save Changes" : "Add Feed"}
                        loadingLabel={initialData ? "Saving..." : "Adding..."}
                    />
                </div>
            </form>
        </Form>
    );
}